import { useCallback, useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import { Building2, Download, Pencil, Plus, RotateCcw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { DataTable } from '../components/ui/data-table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { useApiConfig } from '../hooks/useApiConfig';
import { BACKEND_BASE_URL, BACKEND_DIRECT_ACCESS_CODE } from '../config/backendConfig';
import {
  fetchClients, fetchInternalClients, fetchInternalClientDetail, createInternalClient, updateInternalClient, upsertClientBilling, fetchInternalUserDirectory,
} from '../utils/api';

const ACTIVATION_STATUSES = ['TRIAL', 'ACTIVE', 'DROPPED'];
const EDITABLE_ACTIVATION_STATUSES = ACTIVATION_STATUSES.filter(s => s !== 'DROPPED');
const CREATE_ACTIVATION_STATUSES = EDITABLE_ACTIVATION_STATUSES;
const ACTIVATION_PILL_CLASS = { ACTIVE: 'pill-green', TRIAL: 'pill-amber', DROPPED: 'pill-grey' };

// Mirrors server/src/interfaces/enums.ts BillingCycle / ModeOfPayment.
const BILLING_CYCLES = ['MONTHLY', 'QUARTERLY', 'HALF_YEARLY', 'ANNUAL'];
const MODES_OF_PAYMENT = ['ENACH', 'RTGS'];

const displayValue = value => value || <span className="contact-missing">—</span>;

const formatClientDisplayName = client => (
  client.clientId == null ? client.name : `${client.name} [${client.clientId}]`
);

const formatInvoiceAmount = value => {
  if (value == null || value === '') return <span className="contact-missing">—</span>;
  return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(Number(value));
};

const formatKpiAmount = value => new Intl.NumberFormat('en-IN', {
  style: 'currency', currency: 'INR', maximumFractionDigits: 0,
}).format(value);

// Mirrors the visible clients table columns — same pattern as
// dataEngine.js's exportCampaignExcel (client-side, xlsx package).
const CLIENTS_EXCEL_COLUMNS = [
  { header: 'Client', key: 'name' },
  { header: 'Client ID', key: 'clientId' },
  { header: 'Status', key: 'activationStatus' },
  { header: 'KAM', key: 'kamName' },
  { header: 'Account Executive', key: 'accountExecutiveName' },
  { header: 'GST Number', key: 'gstNumber' },
  { header: 'Monthly Invoice', key: 'monthlyInvoiceAmount' },
  { header: 'CRM ID', key: 'crmId' },
  { header: 'Billing Cycle', key: 'billingCycle' },
  { header: 'Payment Mode', key: 'modeOfPayment' },
  { header: '1st Payment Date', key: 'firstPaymentDate' },
  { header: 'Cohort', key: 'cohort' },
];

function exportClientsExcel(rows) {
  const headers = CLIENTS_EXCEL_COLUMNS.map(c => c.header);
  const wsData = [headers, ...rows.map(r => CLIENTS_EXCEL_COLUMNS.map(c => r[c.key] ?? ''))];
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet(wsData);
  ws['!cols'] = [{ wch: 24 }, { wch: 12 }, { wch: 12 }, { wch: 20 }, { wch: 20 }, { wch: 16 }, { wch: 16 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 16 }, { wch: 10 }];
  XLSX.utils.book_append_sheet(wb, ws, 'Clients');
  const date = new Date().toISOString().split('T')[0];
  XLSX.writeFile(wb, `Clients_${date}.xlsx`);
}

// Indian PAN format: 5 letters, 4 digits, 1 letter (e.g. AAAAA9999A) — mirrors
// the backend's check in server/src/utils/pan.ts, so a bad format is caught
// here before the round trip instead of only after a 400 comes back.
const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]$/;

// Role-specific copy for the Add-client dialog — mirrors the backend's
// ClientOwnerAssignmentStrategy (services/internalUsers/ownerAssignment.ts):
// a KAM/AE is auto-assigned to their own new client, an Admin must pick both.
const ROLE_COPY = {
  KAM: 'You’ll be set as this client’s KAM automatically. Only an admin can assign an Account Executive.',
  ACCOUNT_EXECUTIVE: 'You’ll be set as this client’s Account Executive. No KAM is assigned yet — an admin must add one before this client can go ACTIVE.',
  ADMIN: 'As an admin, you may optionally assign a KAM and an Account Executive.',
};

function Field({ label, hint, required, children }) {
  return (
    <div className="ui-field">
      <label className="form-label">
        {label}
        {required && <span className="required-mark"> *</span>}
      </label>
      {children}
      {hint && <p className="form-hint">{hint}</p>}
    </div>
  );
}

// Dropdown of internal staff for a KAM / Account-Executive slot. Only staff
// whose role matches the slot are offered — a KAM slot lists KAMs, an
// Account-Executive slot lists Account Executives. Option value = email, the
// form the backend's kamEmail/accountExecutiveEmail fields expect.
function OwnerSelect({ label, hint, value, onChange, users, role, required, emptyLabel }) {
  const matching = users.filter(u => u.role === role);
  const display = u => `${u.firstName}${u.lastName ? ` ${u.lastName}` : ''} — ${u.email}`;
  // A currently-assigned owner whose role doesn't match this slot (e.g. a
  // legacy assignment, or an admin who once covered as KAM) would otherwise
  // drop out of the list and render as a blank selection — keep them shown so
  // the existing assignment stays visible until it's explicitly changed.
  const current =
    value && !matching.some(u => u.email === value)
      ? users.find(u => u.email === value) || { email: value }
      : null;
  return (
    <Field label={label} hint={hint} required={required}>
      <select className="form-input" value={value} onChange={e => onChange(e.target.value)} required={required}>
        <option value="">{emptyLabel}</option>
        {matching.map(u => <option key={u.email} value={u.email}>{display(u)}</option>)}
        {current && (
          <option key={current.email} value={current.email}>
            {current.firstName ? display(current) : current.email}
          </option>
        )}
      </select>
    </Field>
  );
}

function AddClientDialog({ actor, internalUsers, onCreate, onDone }) {
  const isAdmin = actor.role === 'ADMIN';
  const [name, setName] = useState('');
  const [panNumber, setPanNumber] = useState('');
  const [sponsorEmail, setSponsorEmail] = useState('');
  const [activationStatus, setActivationStatus] = useState('TRIAL');
  const [kamEmail, setKamEmail] = useState('');
  const [accountExecutiveEmail, setAccountExecutiveEmail] = useState('');
  const [firstPaymentDate, setFirstPaymentDate] = useState('');
  const [gstNumber, setGstNumber] = useState('');
  const [monthlyInvoiceAmount, setMonthlyInvoiceAmount] = useState('');
  const [crmId, setCrmId] = useState('');
  const [billingCycle, setBillingCycle] = useState('');
  const [modeOfPayment, setModeOfPayment] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async event => {
    event.preventDefault();
    const trimmedPan = panNumber.trim().toUpperCase();
    if (!PAN_REGEX.test(trimmedPan)) {
      setError('PAN number is required and must be a valid 10-character PAN (e.g. AAAAA9999A).');
      return;
    }
    if (!sponsorEmail.trim()) {
      setError('Sponsor email is required.');
      return;
    }
    // Mirrors the backend's crmId-required-for-ACTIVE check (clients.ts) —
    // caught here too so the error shows before the round trip.
    if (activationStatus === 'ACTIVE' && !crmId.trim()) {
      setError('CRM Id is required for an ACTIVE client.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const payload = { name: name.trim(), activationStatus, panNumber: trimmedPan, sponsorEmail: sponsorEmail.trim() };
      if (gstNumber.trim()) payload.gstNumber = gstNumber.trim();
      if (monthlyInvoiceAmount.trim()) payload.monthlyInvoiceAmount = Number(monthlyInvoiceAmount);
      if (crmId.trim()) payload.crmId = crmId.trim();
      if (billingCycle) payload.billingCycle = billingCycle;
      if (modeOfPayment) payload.modeOfPayment = modeOfPayment;
      if (firstPaymentDate) payload.firstPaymentDate = firstPaymentDate;
      if (isAdmin) {
        if (kamEmail.trim()) payload.kamEmail = kamEmail.trim();
        if (accountExecutiveEmail.trim()) payload.accountExecutiveEmail = accountExecutiveEmail.trim();
        if (firstPaymentDate) payload.firstPaymentDate = firstPaymentDate;
      }
      await onCreate(payload);
      onDone();
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Add client</DialogTitle>
        <DialogDescription>{ROLE_COPY[actor.role] || ''}</DialogDescription>
      </DialogHeader>
      <form className="ui-form-grid" onSubmit={handleSubmit}>
        <Field label="Brand name" required>
          <Input className="form-input" value={name} onChange={e => setName(e.target.value)} required autoFocus />
        </Field>
        <div className="ui-form-row">
          <Field label="PAN number" hint="Format: AAAAA9999A" required>
            <Input className="form-input" value={panNumber} onChange={e => setPanNumber(e.target.value.toUpperCase())} maxLength={10} required />
          </Field>
          <Field label="Activation status">
            <select className="form-input" value={activationStatus} onChange={e => setActivationStatus(e.target.value)}>
              {CREATE_ACTIVATION_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
        </div>
        <Field label="Sponsor email" hint="Sends an onboarding invite once the client is created." required>
          <Input className="form-input" type="email" value={sponsorEmail} onChange={e => setSponsorEmail(e.target.value)} required />
        </Field>

        {isAdmin && (
          <>
            <div className="ui-form-row">
              <OwnerSelect label="KAM" hint="Optional" value={kamEmail} onChange={setKamEmail} users={internalUsers} role="KAM" emptyLabel="None" />
              <OwnerSelect label="Account Executive" hint="Optional" value={accountExecutiveEmail} onChange={setAccountExecutiveEmail} users={internalUsers} role="ACCOUNT_EXECUTIVE" emptyLabel="None" />
            </div>
            <Field label="1st Payment Date" hint="Optional — used with Kickoff Date to derive the Cohort.">
              <Input className="form-input" type="date" value={firstPaymentDate} onChange={e => setFirstPaymentDate(e.target.value)} />
            </Field>
          </>
        )}

        <p className="ui-form-section-title">Billing Information</p>
        <div className="ui-form-row">
          <Field label="GST number" hint="Optional">
            <Input className="form-input" value={gstNumber} onChange={e => setGstNumber(e.target.value.toUpperCase())} />
          </Field>
          <Field label="Monthly invoice amount" hint="Optional">
            <Input className="form-input" type="number" min="0" step="0.01" value={monthlyInvoiceAmount} onChange={e => setMonthlyInvoiceAmount(e.target.value)} />
          </Field>
        </div>
        <div className="ui-form-row">
          <Field label="CRM Id" hint={activationStatus === 'ACTIVE' ? 'Required for an ACTIVE client' : 'Optional'} required={activationStatus === 'ACTIVE'}>
            <Input className="form-input" value={crmId} onChange={e => setCrmId(e.target.value)} required={activationStatus === 'ACTIVE'} />
          </Field>
          <Field label="Billing cycle" hint="Optional">
            <select className="form-input" value={billingCycle} onChange={e => setBillingCycle(e.target.value)}>
              <option value="">None</option>
              {BILLING_CYCLES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
        </div>
        <Field label="Mode of payment" hint="Optional">
          <select className="form-input" value={modeOfPayment} onChange={e => setModeOfPayment(e.target.value)}>
            <option value="">None</option>
            {MODES_OF_PAYMENT.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </Field>
        <Field label="1st Payment Date" hint="Optional — used together with Kickoff Date to derive the Cohort.">
          <Input className="form-input" type="date" value={firstPaymentDate} onChange={e => setFirstPaymentDate(e.target.value)} />
        </Field>

        {error && <p className="ui-form-error">{error}</p>}
        <DialogFooter>
          <Button type="submit" disabled={submitting || !name.trim() || !panNumber.trim() || !sponsorEmail.trim()}>{submitting ? 'Creating…' : 'Create client'}</Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function EditClientDialog({ client, actor, isAdmin, internalUsers, onUpdate, onBillingUpdate, onDone }) {
  const [activationStatus, setActivationStatus] = useState(client.activationStatus);
  const [name, setName] = useState(client.name || '');
  const [panNumber, setPanNumber] = useState(client.panNumber || '');
  const [sponsorEmail, setSponsorEmail] = useState(client.sponsorEmail || '');
  const [kamEmail, setKamEmail] = useState(client.kamEmail || '');
  const [accountExecutiveEmail, setAccountExecutiveEmail] = useState(client.accountExecutiveEmail || '');
  const [gstNumber, setGstNumber] = useState(client.gstNumber || '');
  const [monthlyInvoiceAmount, setMonthlyInvoiceAmount] = useState(
    client.monthlyInvoiceAmount != null ? String(client.monthlyInvoiceAmount) : '',
  );
  const [crmId, setCrmId] = useState(client.crmId || '');
  const [billingCycle, setBillingCycle] = useState(client.billingCycle || '');
  const [modeOfPayment, setModeOfPayment] = useState(client.modeOfPayment || '');
  const [firstPaymentDate, setFirstPaymentDate] = useState(client.firstPaymentDate || '');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const activationStatusOptions = client.activationStatus === 'DROPPED'
    ? ['DROPPED', ...EDITABLE_ACTIVATION_STATUSES]
    : EDITABLE_ACTIVATION_STATUSES;

  const handleSubmit = async event => {
    event.preventDefault();
    if (isAdmin && !name.trim()) {
      setError('Brand name cannot be empty.');
      return;
    }
    // PAN stays optional to touch here — changing an unrelated field on a
    // legacy client shouldn't force a PAN entry — but whatever's typed must
    // be a valid PAN, since a badly-formatted one would silently break the
    // client's GST-record matching.
    const trimmedPan = panNumber.trim().toUpperCase();
    if (trimmedPan && !PAN_REGEX.test(trimmedPan)) {
      setError('PAN number must be a valid 10-character PAN (e.g. AAAAA9999A).');
      return;
    }
    if (activationStatus === 'DROPPED' && client.activationStatus !== 'DROPPED') {
      setError('Clients cannot be marked as DROPPED.');
      return;
    }
    // Mirrors the backend's crmId-required-on-transition-into-ACTIVE check
    // (clients.ts) — only fires when actually moving into ACTIVE, not for an
    // already-ACTIVE client being re-saved.
    const isActivating = activationStatus === 'ACTIVE' && client.activationStatus !== 'ACTIVE';
    if (isActivating && !crmId.trim()) {
      setError('CRM Id is required to move this client to ACTIVE.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      // Every field is pre-filled with its current value, so only send what
      // actually changed — an unchanged activationStatus must not be
      // re-asserted, or editing a legacy ACTIVE client without a KAM would
      // trip the backend's KAM-before-ACTIVE rule on unrelated edits. PAN
      // and brand name can't be cleared (no "no PAN"/"no name" state), so
      // those two only send when both changed AND non-blank.
      const payload = {};
      if (activationStatus !== client.activationStatus) payload.activationStatus = activationStatus;
      if (isAdmin) {
        if (name.trim() !== (client.name || '')) payload.name = name.trim();
        if (trimmedPan && trimmedPan !== (client.panNumber || '')) payload.panNumber = trimmedPan;
        if (sponsorEmail.trim() !== (client.sponsorEmail || '')) payload.sponsorEmail = sponsorEmail.trim();
        if (kamEmail.trim() !== (client.kamEmail || '')) payload.kamEmail = kamEmail.trim();
        if (accountExecutiveEmail.trim() !== (client.accountExecutiveEmail || '')) {
          payload.accountExecutiveEmail = accountExecutiveEmail.trim();
        }
        if (firstPaymentDate !== (client.firstPaymentDate || '')) {
          payload.firstPaymentDate = firstPaymentDate;
        }
      }
      // Billing has its own endpoint, open to every internal role (non-admins
      // for their own clients — which is all they can see here anyway).
      const billingPayload = {};
      if (gstNumber.trim() !== (client.gstNumber || '')) billingPayload.gstNumber = gstNumber.trim();
      const originalInvoiceAmount = client.monthlyInvoiceAmount != null ? String(client.monthlyInvoiceAmount) : '';
      if (monthlyInvoiceAmount.trim() && monthlyInvoiceAmount.trim() !== originalInvoiceAmount) {
        billingPayload.monthlyInvoiceAmount = Number(monthlyInvoiceAmount);
      }
      if (crmId.trim() !== (client.crmId || '')) billingPayload.crmId = crmId.trim();
      if (billingCycle !== (client.billingCycle || '')) billingPayload.billingCycle = billingCycle;
      if (modeOfPayment !== (client.modeOfPayment || '')) billingPayload.modeOfPayment = modeOfPayment;
      if (isAdmin) {
        // Admin client updates accept billing fields in the same request. This
        // prevents TRIAL -> ACTIVE validation from running before crmId is sent.
        Object.assign(payload, billingPayload);
        if (isActivating) payload.crmId = crmId.trim();
        if (Object.keys(payload).length) {
          await onUpdate(client.clientId, payload);
        }
      } else {
        // Billing must be saved before activation so the backend sees the
        // latest CRM Id when it validates the status transition.
        if (Object.keys(billingPayload).length) {
          await onBillingUpdate(client.clientId, billingPayload);
        }
        if (Object.keys(payload).length) {
          await onUpdate(client.clientId, payload);
        }
      }
      onDone();
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{formatClientDisplayName(client)}</DialogTitle>
        <DialogDescription>
          {isAdmin
            ? 'Fields are pre-filled with their current values — only edit what you want to change.'
            : 'You can update this client’s activation status and billing info.'}
        </DialogDescription>
      </DialogHeader>
      <form className="ui-form-grid" onSubmit={handleSubmit}>
        <Field label="Activation status">
          <select className="form-input" value={activationStatus} onChange={e => setActivationStatus(e.target.value)}>
            {activationStatusOptions.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>

        {isAdmin && (
          <>
            <div className="ui-form-row">
              <Field label="Brand name" required>
                <Input className="form-input" value={name} onChange={e => setName(e.target.value)} required />
              </Field>
              <Field label="PAN number" hint="Format: AAAAA9999A">
                <Input className="form-input" value={panNumber} onChange={e => setPanNumber(e.target.value.toUpperCase())} maxLength={10} />
              </Field>
            </div>
            <Field label="Sponsor email">
              <Input className="form-input" type="email" value={sponsorEmail} onChange={e => setSponsorEmail(e.target.value)} />
            </Field>
            <div className="ui-form-row">
              <OwnerSelect label="Reassign KAM" value={kamEmail} onChange={setKamEmail} users={internalUsers} role="KAM" emptyLabel="Keep current" />
              <OwnerSelect label="Reassign Account Executive" value={accountExecutiveEmail} onChange={setAccountExecutiveEmail} users={internalUsers} role="ACCOUNT_EXECUTIVE" emptyLabel="Keep current" />
            </div>
            <Field label="1st Payment Date" hint="Used together with Kickoff Date to derive the Cohort.">
              <Input className="form-input" type="date" value={firstPaymentDate} onChange={e => setFirstPaymentDate(e.target.value)} />
            </Field>
          </>
        )}

        <p className="ui-form-section-title">Billing Information</p>
        <div className="ui-form-row">
          <Field label="GST number">
            <Input className="form-input" value={gstNumber} onChange={e => setGstNumber(e.target.value.toUpperCase())} />
          </Field>
          <Field label="Monthly invoice amount">
            <Input className="form-input" type="number" min="0" step="0.01" value={monthlyInvoiceAmount} onChange={e => setMonthlyInvoiceAmount(e.target.value)} />
          </Field>
        </div>
        <div className="ui-form-row">
          <Field label="CRM Id" hint={activationStatus === 'ACTIVE' ? 'Required for an ACTIVE client' : 'Optional'} required={activationStatus === 'ACTIVE'}>
            <Input className="form-input" value={crmId} onChange={e => setCrmId(e.target.value)} required={activationStatus === 'ACTIVE'} />
          </Field>
          <Field label="Billing cycle">
            <select className="form-input" value={billingCycle} onChange={e => setBillingCycle(e.target.value)}>
              <option value="">None</option>
              {BILLING_CYCLES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
        </div>
        <Field label="Mode of payment">
          <select className="form-input" value={modeOfPayment} onChange={e => setModeOfPayment(e.target.value)}>
            <option value="">None</option>
            {MODES_OF_PAYMENT.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </Field>

        {error && <p className="ui-form-error">{error}</p>}
        <DialogFooter>
          <Button type="submit" disabled={submitting}>{submitting ? 'Saving…' : 'Save changes'}</Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

export default function ClientsPage() {
  const { currentUser } = useAuth();
  const cfg = useApiConfig();
  const isAdmin = currentUser?.role === 'ADMIN';

  const [clients, setClients] = useState(null);
  const [internalUsers, setInternalUsers] = useState([]);
  const [error, setError] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detailLoadingId, setDetailLoadingId] = useState(null);

  // The row objects from fetchInternalClients are deliberately lightweight
  // (name/status/owner display-names only) — fetch the full editable detail
  // (PAN, sponsor email, GST/billing, owner emails) before opening the dialog
  // so every field can be pre-filled with its real current value.
  const openEdit = useCallback(async c => {
    setDetailLoadingId(c.clientId);
    setError(null);
    try {
      setEditing(await fetchInternalClientDetail(BACKEND_BASE_URL, cfg, c.clientId));
    } catch (e) {
      setError(e.message);
    } finally {
      setDetailLoadingId(null);
    }
  }, [cfg]);

  const load = useCallback(async () => {
    if (!cfg) return;
    setError(null);
    try {
      // The deployed internal-client endpoint predates its server-side
      // EXTERNAL restriction. Cross-check against the existing campaign
      // endpoint so local/new frontends never display an INTERNAL account
      // while the backend release is rolling out. Once the updated backend
      // is deployed, clientType provides the same check directly.
      const [internalClients, externalClients] = await Promise.all([
        fetchInternalClients(BACKEND_BASE_URL, cfg),
        fetchClients(BACKEND_BASE_URL, { directAccessCode: BACKEND_DIRECT_ACCESS_CODE }),
      ]);
      const externalClientIds = new Set(externalClients.map(c => Number(c.client_id)));
      setClients(internalClients.filter(c =>
        c.clientType === 'EXTERNAL' || externalClientIds.has(Number(c.clientId)),
      ));
    } catch (e) {
      setError(e.message);
      return;
    }
    // The staff directory (readable by every internal role) feeds the KAM/AE
    // dropdowns in the dialogs — a failure here degrades to empty dropdowns,
    // not a reason to hide the client list.
    try {
      setInternalUsers(await fetchInternalUserDirectory(BACKEND_BASE_URL, cfg));
    } catch {
      setInternalUsers([]);
    }
  }, [cfg]);

  useEffect(() => { load(); }, [load]);

  const rows = clients || [];

  const clientKpis = useMemo(() => {
    const list = clients || [];
    const billingFields = ['gstNumber', 'monthlyInvoiceAmount', 'crmId', 'billingCycle', 'modeOfPayment'];
    return {
      total: list.length,
      active: list.filter(c => c.activationStatus === 'ACTIVE').length,
      trial: list.filter(c => c.activationStatus === 'TRIAL').length,
      activeWithoutKam: list.filter(c => c.activationStatus === 'ACTIVE' && !String(c.kamName || '').trim()).length,
      monthlyInvoiceTotal: list.reduce((sum, c) => sum + (Number(c.monthlyInvoiceAmount) || 0), 0),
      billingComplete: list.filter(c => billingFields.every(field => c[field] != null && c[field] !== '')).length,
    };
  }, [clients]);

  const clientColumns = useMemo(() => [
    {
      id: 'name', accessorFn: formatClientDisplayName, header: 'Client', filterFn: 'includesString',
      meta: { label: 'Client', filterVariant: 'text' },
      cell: ({ row }) => <span className="user-name">{formatClientDisplayName(row.original)}</span>,
    },
    {
      accessorKey: 'activationStatus', header: 'Status', filterFn: 'equalsString',
      meta: { label: 'Status', filterVariant: 'select', filterOptions: ACTIVATION_STATUSES },
      cell: ({ row }) => (
        <span className={`pill ${ACTIVATION_PILL_CLASS[row.original.activationStatus] || 'pill-grey'}`}>
          {row.original.activationStatus}
        </span>
      ),
    },
    {
      accessorKey: 'kamName', header: 'KAM', filterFn: 'equalsString', meta: { label: 'KAM', filterVariant: 'select' },
      cell: ({ row }) => row.original.kamName
        ? <span className="client-chip">{row.original.kamName}</span>
        : <span className="contact-missing">—</span>,
    },
    {
      accessorKey: 'accountExecutiveName', header: 'Account Executive', filterFn: 'equalsString',
      meta: { label: 'Account Executive', filterVariant: 'select' },
      cell: ({ row }) => row.original.accountExecutiveName
        ? <span className="client-chip">{row.original.accountExecutiveName}</span>
        : <span className="contact-missing">—</span>,
    },
    {
      accessorKey: 'gstNumber', header: 'GST Number', filterFn: 'includesString',
      meta: { label: 'GST Number', filterVariant: 'text' },
      cell: ({ row }) => <span className="billing-value">{displayValue(row.original.gstNumber)}</span>,
    },
    {
      accessorKey: 'monthlyInvoiceAmount', header: 'Monthly Invoice', filterFn: 'numberRange',
      meta: { label: 'Monthly Invoice', filterVariant: 'numberRange' },
      cell: ({ row }) => <span className="billing-value billing-amount">{formatInvoiceAmount(row.original.monthlyInvoiceAmount)}</span>,
    },
    {
      accessorKey: 'crmId', header: 'CRM ID', filterFn: 'includesString', meta: { label: 'CRM ID', filterVariant: 'text' },
      cell: ({ row }) => <span className="billing-value">{displayValue(row.original.crmId)}</span>,
    },
    {
      accessorKey: 'billingCycle', header: 'Billing Cycle', filterFn: 'equalsString',
      meta: { label: 'Billing Cycle', filterVariant: 'select', filterOptions: BILLING_CYCLES },
      cell: ({ row }) => displayValue(row.original.billingCycle),
    },
    {
      accessorKey: 'modeOfPayment', header: 'Payment Mode', filterFn: 'equalsString',
      meta: { label: 'Payment Mode', filterVariant: 'select', filterOptions: MODES_OF_PAYMENT },
      cell: ({ row }) => displayValue(row.original.modeOfPayment),
    },
    {
      accessorKey: 'firstPaymentDate', header: '1st Payment Date', filterFn: 'dateRange',
      meta: { label: '1st Payment Date', filterVariant: 'dateRange' },
      cell: ({ row }) => displayValue(row.original.firstPaymentDate),
    },
    {
      accessorKey: 'cohort', header: 'Cohort', filterFn: 'equalsString',
      meta: { label: 'Cohort', filterVariant: 'select' },
      cell: ({ row }) => displayValue(row.original.cohort),
    },
    {
      id: 'actions', header: '', enableSorting: false, enableColumnFilter: false, enableHiding: false,
      cell: ({ row }) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => openEdit(row.original)}
          disabled={detailLoadingId === row.original.clientId}
        >
          <Pencil size={14} />{detailLoadingId === row.original.clientId ? 'Loading…' : 'Edit'}
        </Button>
      ),
    },
  ], [detailLoadingId, openEdit]);

  if (!cfg) return null;

  return (
    <div className="page-content">
      <div className="section-header">
        <Building2 size={18} />
        <h2>Clients</h2>
        <span className="row-count">{clients ? `${rows.length} of ${clients.length}` : '…'}</span>
        <div className="header-right">
          <Button variant="outline" size="sm" onClick={load}><RotateCcw size={15} />Refresh</Button>
          <Button variant="outline" size="sm" onClick={() => exportClientsExcel(rows)} disabled={!rows.length}>
            <Download size={15} />Download Excel
          </Button>
          <Dialog open={addOpen} onOpenChange={setAddOpen}>
            <DialogTrigger asChild><Button size="sm"><Plus size={15} />Add client</Button></DialogTrigger>
            {addOpen && currentUser && (
              <AddClientDialog
                actor={currentUser}
                internalUsers={internalUsers}
                onCreate={payload => createInternalClient(BACKEND_BASE_URL, cfg, payload)}
                onDone={() => { setAddOpen(false); load(); }}
              />
            )}
          </Dialog>
        </div>
      </div>
      <p className="role-banner">
        Signed in as <strong>{currentUser?.name}</strong> ({currentUser?.role}).{' '}
        {isAdmin ? 'You can see and manage every client.' : 'Showing only clients where you’re the KAM or Account Executive.'}
      </p>

      {clients && (
        <div className="client-kpi-grid" aria-label="Client KPIs">
          <div className="client-kpi-card client-kpi-overview">
            <span className="client-kpi-label">Client Overview</span>
            <div className="client-kpi-breakdown">
              <div><strong>{clientKpis.total}</strong><small>External</small></div>
              <div><strong>{clientKpis.active}</strong><small>Active</small></div>
              <div><strong>{clientKpis.trial}</strong><small>Trial</small></div>
            </div>
          </div>
          <div className="client-kpi-card client-kpi-unassigned">
            <span className="client-kpi-label">Active without KAM</span>
            <strong>{clientKpis.activeWithoutKam}</strong>
            <small>Needs KAM assignment</small>
          </div>
          <div className="client-kpi-card client-kpi-billing">
            <span className="client-kpi-label">Monthly Invoicing</span>
            <strong className="client-kpi-money">{formatKpiAmount(clientKpis.monthlyInvoiceTotal)}</strong>
            <small>Across all clients</small>
          </div>
          <div className="client-kpi-card client-kpi-complete">
            <span className="client-kpi-label">Billing Complete</span>
            <strong>{clientKpis.billingComplete}<span> / {clientKpis.total}</span></strong>
            <small>All billing fields captured</small>
          </div>
        </div>
      )}

      {error && <p className="error-msg">{error}</p>}
      {!clients && !error && <div className="loading-state"><div className="spinner" /><p>Loading clients…</p></div>}

      {clients && <DataTable columns={clientColumns} data={rows} emptyMessage="No clients match the selected filters." />}

      {editing && (
        <Dialog open onOpenChange={open => !open && setEditing(null)}>
          <EditClientDialog
            client={editing}
            actor={currentUser}
            isAdmin={isAdmin}
            internalUsers={internalUsers}
            onUpdate={(clientId, payload) => updateInternalClient(BACKEND_BASE_URL, cfg, clientId, payload)}
            onBillingUpdate={(clientId, payload) => upsertClientBilling(BACKEND_BASE_URL, cfg, clientId, payload)}
            onDone={() => { setEditing(null); load(); }}
          />
        </Dialog>
      )}
    </div>
  );
}
